import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Calendar,
  MapPin,
  Clock,
  Users,
  Video,
  Globe,
  DollarSign,
  Ticket,
  ChevronRight,
  X,
  Loader2,
  Check,
  AlertCircle,
  ExternalLink,
  Lock
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_TYPE_ICONS = {
  in_person: MapPin,
  online: Video,
  hybrid: Globe
};

// Event Detail Modal with Registration
const EventDetailModal = ({ event, onClose, onRegistered }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    guests: 0,
    notes: ''
  });
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const TypeIcon = EVENT_TYPE_ICONS[event.event_type] || MapPin;

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleRegister = async () => {
    if (!formData.name || !formData.email) {
      setError('Please enter your name and email');
      return;
    }

    setRegistering(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/events/${event.id}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        if (data.requires_payment && data.checkout_url) {
          // Redirect to Stripe checkout
          window.location.href = data.checkout_url;
        } else {
          setSuccess(true);
          setTimeout(() => {
            onRegistered();
            onClose();
          }, 2000);
        }
      } else {
        setError(data.detail || 'Registration failed');
      }
    } catch (err) {
      setError('Registration failed. Please try again.');
    } finally {
      setRegistering(false);
    }
  };

  // Calculate price
  const price = user?.membership_tier && event.member_price !== null 
    ? event.member_price 
    : event.price;
  const totalPrice = price * (formData.guests + 1);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="relative">
          {event.image_url ? (
            <img src={event.image_url} alt={event.title} className="w-full h-48 object-cover" />
          ) : (
            <div className="w-full h-48 bg-gradient-to-br from-lws-green to-lws-navy" />
          )}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6">
          {success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-serif text-stone-800 mb-2">You&apos;re Registered!</h3>
              <p className="text-stone-500">We&apos;ll send confirmation details to your email.</p>
            </div>
          ) : (
            <>
              {/* Event Info */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  {event.is_members_only && (
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                      Members Only
                    </span>
                  )}
                  {event.chapter && (
                    <span className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full">
                      {event.chapter} Chapter
                    </span>
                  )}
                </div>
                <h2 className="text-2xl font-serif text-stone-800 mb-4">{event.title}</h2>
                
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-3 text-stone-600">
                    <Calendar className="w-5 h-5 text-lws-green" />
                    <span>{formatDate(event.date)}</span>
                  </div>
                  <div className="flex items-center gap-3 text-stone-600">
                    <Clock className="w-5 h-5 text-lws-green" />
                    <span>{event.time}{event.end_time && ` - ${event.end_time}`}</span>
                  </div>
                  <div className="flex items-center gap-3 text-stone-600">
                    <TypeIcon className="w-5 h-5 text-lws-green" />
                    <span>
                      {event.event_type === 'in_person' && event.location}
                      {event.event_type === 'online' && 'Online Event'}
                      {event.event_type === 'hybrid' && `Hybrid: ${event.location || 'TBA'}`}
                    </span>
                  </div>
                  {event.capacity && (
                    <div className="flex items-center gap-3 text-stone-600">
                      <Users className="w-5 h-5 text-lws-green" />
                      <span>
                        {event.registration_count || 0} / {event.capacity} spots filled
                        {event.is_sold_out && <span className="text-red-500 ml-2">(Sold Out)</span>}
                      </span>
                    </div>
                  )}
                </div>

                {event.description && (
                  <p className="mt-4 text-stone-600 text-sm leading-relaxed">{event.description}</p>
                )}
              </div>

              {/* Registration Form */}
              {event.is_sold_out ? (
                <div className="bg-stone-100 rounded-lg p-4 text-center">
                  <p className="text-stone-600">This event is sold out.</p>
                </div>
              ) : event.is_members_only && !user ? (
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <Lock className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                  <p className="text-purple-700 font-medium">Members Only Event</p>
                  <p className="text-purple-600 text-sm mt-1">Please log in to register.</p>
                  <a href="#login" className="inline-block mt-3 text-sm text-purple-700 underline">
                    Log In
                  </a>
                </div>
              ) : (
                <div className="space-y-4">
                  <h3 className="font-medium text-stone-800">Register for this event</h3>
                  
                  <div>
                    <label className="block text-sm text-stone-600 mb-1">Your Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
                      className="w-full px-4 py-2 border border-stone-200 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-stone-600 mb-1">Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
                      className="w-full px-4 py-2 border border-stone-200 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-stone-600 mb-1">Additional Guests</label>
                    <select
                      value={formData.guests}
                      onChange={(e) => setFormData(f => ({ ...f, guests: parseInt(e.target.value) }))}
                      className="w-full px-4 py-2 border border-stone-200 rounded-lg"
                    >
                      {[0, 1, 2, 3, 4, 5].map(n => (
                        <option key={n} value={n}>{n} {n === 0 ? '(just me)' : `guest${n > 1 ? 's' : ''}`}</option>
                      ))}
                    </select>
                  </div>

                  {/* Price Summary */}
                  {price > 0 && (
                    <div className="bg-stone-50 rounded-lg p-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-stone-600">
                          ${price.toFixed(2)} × {formData.guests + 1} {formData.guests === 0 ? 'person' : 'people'}
                        </span>
                        <span className="font-medium text-stone-800">${totalPrice.toFixed(2)} AUD</span>
                      </div>
                      {user?.membership_tier && event.member_price !== null && event.member_price < event.price && (
                        <p className="text-xs text-green-600 mt-1">Member discount applied!</p>
                      )}
                    </div>
                  )}

                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {error}
                    </div>
                  )}

                  <Button
                    onClick={handleRegister}
                    disabled={registering}
                    className="w-full bg-lws-green hover:bg-lws-green/90"
                  >
                    {registering ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Ticket className="w-4 h-4 mr-2" />
                    )}
                    {price > 0 ? `Register & Pay $${totalPrice.toFixed(2)}` : 'Register (Free)'}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// Event Card Component
const EventCard = ({ event, onClick }) => {
  const TypeIcon = EVENT_TYPE_ICONS[event.event_type] || MapPin;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return {
      day: date.getDate(),
      month: date.toLocaleDateString('en-AU', { month: 'short' }),
      weekday: date.toLocaleDateString('en-AU', { weekday: 'short' })
    };
  };

  const dateInfo = formatDate(event.date);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -4 }}
      onClick={onClick}
      className="bg-white rounded-xl border border-stone-200 overflow-hidden cursor-pointer group"
    >
      {/* Date Badge */}
      <div className="flex">
        <div className="w-20 bg-lws-green text-white flex flex-col items-center justify-center py-4">
          <span className="text-xs uppercase">{dateInfo.month}</span>
          <span className="text-3xl font-serif">{dateInfo.day}</span>
          <span className="text-xs">{dateInfo.weekday}</span>
        </div>
        
        <div className="flex-1 p-5">
          <div className="flex items-center gap-2 mb-2">
            {event.is_members_only && (
              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                Members
              </span>
            )}
            {event.chapter && (
              <span className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full">
                {event.chapter}
              </span>
            )}
          </div>
          
          <h3 className="font-serif text-lg text-stone-800 group-hover:text-lws-green transition-colors mb-2">
            {event.title}
          </h3>
          
          <div className="flex flex-wrap items-center gap-4 text-sm text-stone-500">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {event.time}
            </span>
            <span className="flex items-center gap-1">
              <TypeIcon className="w-4 h-4" />
              {event.event_type === 'online' ? 'Online' : event.location?.split(',')[0] || 'TBA'}
            </span>
            {event.price > 0 ? (
              <span className="flex items-center gap-1 text-green-600">
                <DollarSign className="w-4 h-4" />
                ${event.price}
              </span>
            ) : (
              <span className="text-blue-600">Free</span>
            )}
          </div>
        </div>

        <div className="flex items-center pr-4">
          <ChevronRight className="w-5 h-5 text-stone-300 group-hover:text-lws-green transition-colors" />
        </div>
      </div>
    </motion.div>
  );
};

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await fetch(`${API_URL}/api/events/list`);
      const data = await response.json();
      setEvents(data.events || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-lws-cream" data-testid="events-page">
      {/* Hero */}
      <section className="relative py-20 px-6 bg-gradient-to-b from-lws-paper to-lws-cream">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-xs uppercase tracking-[0.3em] text-lws-gold font-medium mb-4">
              Letter Writers Society
            </p>
            <h1 className="text-4xl md:text-5xl font-serif text-lws-navy mb-6">
              Upcoming Gatherings
            </h1>
            <p className="text-lg text-lws-taupe max-w-2xl mx-auto">
              Join us for letter writing workshops, chapter meetups, and community events. 
              Connect with fellow correspondents in person and online.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Events List */}
      <section className="py-12 px-6">
        <div className="max-w-3xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-stone-200">
              <Calendar className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <h3 className="text-lg font-serif text-stone-600">No upcoming events</h3>
              <p className="text-stone-400 mt-1">Check back soon for new gatherings!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {events.map(event => (
                <EventCard
                  key={event.id}
                  event={event}
                  onClick={() => setSelectedEvent(event)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Chapter CTA */}
      <section className="py-16 px-6 bg-lws-navy text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-serif mb-4">Start a Chapter in Your City</h2>
          <p className="text-white/70 mb-8 max-w-2xl mx-auto">
            Want to bring Letter Writers Society to your community? 
            Become a Postmaster and host your own local gatherings.
          </p>
          <a
            href="#hosting-application"
            className="inline-flex items-center gap-2 px-6 py-3 bg-lws-gold text-white rounded-lg hover:bg-lws-gold/90 transition-colors"
          >
            Apply to be a Postmaster
            <ChevronRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Event Detail Modal */}
      <AnimatePresence>
        {selectedEvent && (
          <EventDetailModal
            event={selectedEvent}
            onClose={() => setSelectedEvent(null)}
            onRegistered={fetchEvents}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
